import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

const CalendarModal = ({ show, onHide, eventType }) => {
  const { t, language } = useLanguage();
  const ceremonyTime = new Date('March 28, 2026 20:15:00 GMT-03:00');
  const partyTime = new Date('March 28, 2026 21:30:00  GMT-03:00');
  let eventTime, eventTitle, eventDescription, eventLocation;
  

  const ceremony = {
    location: t('weddingDetails.ceremony.location'),
    address: t('weddingDetails.ceremony.address'),
    time: t('weddingDetails.ceremony.time'),
    title: t('weddingDetails.ceremony.title'),
    description: t('weddingDetails.ceremony.description'),
  }
  const reception = {
    location: t('weddingDetails.reception.location'),
    address: t('weddingDetails.reception.address'),
    time: t('weddingDetails.reception.time'),
    title: t('weddingDetails.reception.title'),
    description: t('weddingDetails.reception.description'),
  }
  if (eventType === 'ceremony') {
    eventTime = ceremonyTime;
    eventTitle = language === 'es' ? 'Ceremonia de Cami & Eze' : 'Cami & Eze Wedding Ceremony';
    eventDescription = language === 'es' ? 'Únete a nosotros para nuestra boda' : 'Join us for our wedding ceremony as we begin our journey together as husband and wife.';
    eventLocation = `${ceremony.location}, ${ceremony.address}`;
  } else {
    eventTime = partyTime;
    eventTitle = language === 'es' ? 'Fiesta de Cami & Eze' : 'Cami & Eze Wedding Party';
    eventDescription = language === 'es' ? 'Celebramos nuestro dia especial con cena, baile. Por favor únete a nuestra fiesta' : 'Celebrate our special day with dinner, dancing, and joy. Please join us for the reception party.';
    eventLocation = `${reception.location}, ${reception.address}`;;
  }
  
  const eventData = {
    title: eventTitle,
    description: eventDescription,
    location: eventLocation,
    startTime: eventTime.toISOString(),
    endTime: new Date(eventTime.getTime() + (eventType === 'ceremony' ? 60 : 480) * 60000).toISOString()
  };

  const downloadICS = () => {
    const start = new Date(eventData.startTime);
    const end = new Date(eventData.endTime);
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Wedding Website//Calendar Event//EN',
      'BEGIN:VEVENT',
      `DTSTART:${start.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
      `DTEND:${end.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
      `SUMMARY:${eventData.title}`,
      `DESCRIPTION:${eventData.description}`,
      `LOCATION:${eventData.location}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    
    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wedding-event.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    onHide();
  };

  const openGoogleCalendar = () => {
    const start = new Date(eventData.startTime);
    const end = new Date(eventData.endTime);
    
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventData.title)}&details=${encodeURIComponent(eventData.description)}&location=${encodeURIComponent(eventData.location)}&dates=${start.toISOString().replace(/[-:]/g, '').split('.')[0]}Z/${end.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;
    
    window.open(googleUrl, '_blank');
    onHide();
  };

  const openOutlookCalendar = () => {
    const start = new Date(eventData.startTime);
    const end = new Date(eventData.endTime);
    
    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(eventData.title)}&body=${encodeURIComponent(eventData.description)}&location=${encodeURIComponent(eventData.location)}&startdt=${start.toISOString()}&enddt=${end.toISOString()}`;
    
    window.open(outlookUrl, '_blank');
    onHide();
  };

  const calendarOptions = [
    {
      icon: 'fas fa-download',
      label: 'Download ICS File',
      onClick: downloadICS,
      color: 'primary'
    },
    {
      icon: 'fab fa-google',
      label: 'Google Calendar',
      onClick: openGoogleCalendar,
      color: 'success'
    },
    {
      icon: 'fas fa-envelope',
      label: 'Outlook',
      onClick: openOutlookCalendar,
      color: 'info'
    },
    {
      icon: 'fab fa-apple',
      label: 'Apple Calendar',
      onClick: downloadICS,
      color: 'secondary'
    }
  ];

  if (!show) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div 
        className="calendar-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onHide}
      >
        <motion.div 
          className="calendar-modal-content"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="calendar-modal-header">
            <h4>Add to Calendar</h4>
            <button className="calendar-modal-close" onClick={onHide}>
              &times;
            </button>
          </div>
          
          <div className="calendar-modal-body">
            <p className="text-muted mb-3">Choose your preferred calendar application:</p>
            
            <div className="calendar-options">
              {calendarOptions.map((option, index) => (
                <motion.button
                  key={index}
                  className={`calendar-option-btn btn btn-outline-${option.color}`}
                  onClick={option.onClick}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <i className={`${option.icon} me-2`}></i>
                  {option.label}
                </motion.button>
              ))}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CalendarModal;
